// Mini recommendations carousel: one card at a time, ring pagination below (snippets/ring-pagination.liquid).
// The active ring's CSS animation drives autoplay: its `animationend` shows the next card. Autoplay is paused
// by the visitor (active dot), by pointer hover or keyboard focus on the block, by a hidden tab or when the
// block is off screen, and never runs under prefers-reduced-motion.
// The element is created when Dawn's product-recommendations element injects the markup, so it may be
// connected many times; everything lives inside the guard.
if (!customElements.get('product-mini-carousel')) {
  const RING_ANIMATION = 'ring-pagination-fill';
  const MIN_VISIBLE = 0.25;

  customElements.define(
    'product-mini-carousel',
    class ProductMiniCarousel extends HTMLElement {
      constructor() {
        super();
        this.pauseReasons = { user: false, hover: false, focus: false, hidden: false, offscreen: false };
        this.current = 0;
        this.touchX = 0;

        this.onPrev = () => this.goTo(this.current - 1, true);
        this.onNext = () => this.goTo(this.current + 1, true);
        this.onDot = (event) => this.handleDot(event);
        this.onRingEnd = (event) => {
          if (event.animationName !== RING_ANIMATION || event.target !== this.rings[this.current]) return;
          this.goTo(this.current + 1, false);
        };
        this.onEnter = () => this.setPauseReason('hover', true);
        this.onLeave = () => this.setPauseReason('hover', false);
        this.onFocusIn = (event) => {
          let keyboard = true;
          try {
            keyboard = event.target.matches(':focus-visible');
          } catch (error) {
            keyboard = true;
          }
          this.setPauseReason('focus', keyboard);
        };
        this.onFocusOut = (event) => {
          if (!this.contains(event.relatedTarget)) this.setPauseReason('focus', false);
        };
        this.onVisibility = () => this.setPauseReason('hidden', document.hidden);
        this.onMotionChange = () => this.syncAutoplay();
        this.onTouchStart = (event) => (this.touchX = event.changedTouches[0].clientX);
        this.onTouchEnd = (event) => {
          const delta = event.changedTouches[0].clientX - this.touchX;
          if (Math.abs(delta) > 50) delta < 0 ? this.onNext() : this.onPrev();
        };
      }

      connectedCallback() {
        this.slides = Array.from(this.querySelectorAll('.mini-recs__slide'));
        this.dots = Array.from(this.querySelectorAll('.ring-pagination__dot'));
        this.rings = this.dots.map((dot) => dot.querySelector('.ring-pagination__ring-progress'));
        this.dotLabels = this.dots.map((dot) => dot.getAttribute('aria-label'));
        this.status = this.querySelector('[data-mini-status]');
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

        // Settings live on the host block, outside the injected markup.
        const host = this.closest('.mini-recs');
        this.autoplaySetting = host?.dataset.autoplay !== 'false';
        this.speed = (parseInt(host?.dataset.speed, 10) || 5) * 1000;
        this.style.setProperty('--ring-duration', `${this.speed}ms`);

        if (this.slides.length < 2) return;

        this.querySelector('.ring-pagination__arrow--prev')?.addEventListener('click', this.onPrev);
        this.querySelector('.ring-pagination__arrow--next')?.addEventListener('click', this.onNext);
        this.dots.forEach((dot) => dot.addEventListener('click', this.onDot));
        this.addEventListener('animationend', this.onRingEnd);
        this.addEventListener('mouseenter', this.onEnter);
        this.addEventListener('mouseleave', this.onLeave);
        this.addEventListener('focusin', this.onFocusIn);
        this.addEventListener('focusout', this.onFocusOut);
        this.addEventListener('touchstart', this.onTouchStart, { passive: true });
        this.addEventListener('touchend', this.onTouchEnd, { passive: true });
        document.addEventListener('visibilitychange', this.onVisibility);
        this.reducedMotion.addEventListener('change', this.onMotionChange);

        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(
            (entries) => this.setPauseReason('offscreen', entries[entries.length - 1].intersectionRatio < MIN_VISIBLE),
            { threshold: MIN_VISIBLE }
          );
          this.observer.observe(this);
        }

        this.pauseReasons.hidden = document.hidden;
        this.syncAutoplay();
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        document.removeEventListener('visibilitychange', this.onVisibility);
        this.reducedMotion?.removeEventListener('change', this.onMotionChange);
      }

      get autoplaying() {
        return this.autoplaySetting && !this.reducedMotion.matches;
      }

      goTo(index, fromUser) {
        const total = this.slides.length;
        const next = (index + total) % total;
        if (next === this.current) return;

        this.slides.forEach((slide, i) => {
          const active = i === next;
          slide.classList.toggle('is-active', active);
          slide.toggleAttribute('inert', !active);
          if (active) slide.removeAttribute('aria-hidden');
          else slide.setAttribute('aria-hidden', 'true');
        });
        this.current = next;
        this.updateDots();
        this.restartRing();

        if (fromUser && this.status) {
          this.status.textContent = `${this.dataset.slideLabel} ${next + 1} ${this.dataset.ofLabel} ${total}`;
        }
      }

      handleDot(event) {
        const index = Number(event.currentTarget.dataset.index);
        if (index === this.current) {
          // While the ring drives autoplay the active dot is the pause / play control.
          if (this.autoplaying) this.setUserPaused(!this.pauseReasons.user);
          return;
        }
        this.goTo(index, true);
      }

      setUserPaused(paused) {
        this.pauseReasons.user = paused;
        if (!paused) {
          this.pauseReasons.focus = false;
          this.pauseReasons.hover = false;
        }
        this.syncPause();
        this.updateDots();
      }

      setPauseReason(reason, value) {
        if (this.pauseReasons[reason] === value) return;
        this.pauseReasons[reason] = value;
        this.syncPause();
      }

      syncPause() {
        this.classList.toggle('is-paused', Object.values(this.pauseReasons).some(Boolean));
        this.classList.toggle('is-user-paused', this.pauseReasons.user && this.autoplaying);
        void this.offsetWidth;
      }

      syncAutoplay() {
        this.classList.toggle('is-autoplaying', this.autoplaying);
        this.syncPause();
        this.updateDots();
        this.restartRing();
      }

      updateDots() {
        this.dots.forEach((dot, i) => {
          const active = i === this.current;
          if (active) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');

          let label = this.dotLabels[i];
          if (active && this.autoplaying) {
            label = this.pauseReasons.user ? this.dataset.playLabel : this.dataset.pauseLabel;
          }
          dot.setAttribute('aria-label', label);
        });
      }

      restartRing() {
        this.rings.forEach((ring) => ring?.classList.remove('is-running'));
        if (!this.autoplaying) return;
        void this.offsetWidth;
        this.rings[this.current]?.classList.add('is-running');
      }
    }
  );
}
